// Keyword-targeted top-of-funnel guides. Rendered client-side with per-post SEO + Article JSON-LD.
export const POSTS = [
  {
    slug: "custom-world-cup-hoodies-2026",
    title: "Custom World Cup Hoodies: The 2026 Fan Guide",
    seoTitle: "Custom World Cup Hoodies — 2026 Fan Guide | Jersey Blend",
    description:
      "Everything you need to know about custom 2026 World Cup hoodies: how to pick your nation, personalize the back with your name and number, and choose the right garment color.",
    keywords:
      "custom World Cup hoodies, 2026 World Cup fan gear, custom soccer jersey, national team hoodie, personalized soccer hoodie with name and number",
    date: "2026-06-01",
    hero: "https://images.unsplash.com/photo-1558151748-f2621b5e52f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    excerpt:
      "The 2026 World Cup is here. Here's how to build a custom hoodie that reps your nation — with your own name and number on the back.",
    related: { label: "Shop Soccer Jerseys", to: "/collections/soccer" },
    sections: [
      {
        h: "Why a custom World Cup hoodie beats a stock jersey",
        p: [
          "Official jerseys look the same on everyone. A custom World Cup hoodie lets you rep your nation your way — original national-team crest artwork on a premium hoodie or tee, finished with your own name and number on the back like a real kit.",
          "It's warm enough for a stadium night, comfortable enough for a watch party, and personal enough to become the piece you reach for every match day of the 2026 tournament.",
        ],
      },
      {
        h: "Step 1 — Pick your nation",
        p: [
          "We carry original fan-made crests for dozens of national teams — Brazil, Argentina, France, Germany, Spain, England and many more. Each design pairs the country's colors with an original emblem, so you're supporting your side without a licensed logo in sight.",
        ],
      },
      {
        h: "Step 2 — Add your name & number",
        p: [
          "Every design is personalizable. Type the name and number you want and we print it, arched, across the back — exactly like a matchday shirt. Put your own name up, or honor your favorite player.",
        ],
      },
      {
        h: "Step 3 — Choose your color",
        p: [
          "Pick from seven garment colors — Black, Navy, Charcoal, Maroon, Sport Grey, Red and White — so your hoodie matches your national colors or your personal style. Hover any product to preview it in each color.",
        ],
      },
    ],
  },
  {
    slug: "gift-ideas-for-nba-fans",
    title: "Gift Ideas for NBA Fans: Personalized Hoodies & Tees",
    seoTitle: "Gift Ideas for NBA Fans — Personalized Hoodies & Tees | Jersey Blend",
    description:
      "Stuck on what to get the basketball fan in your life? Personalized NBA hoodies and tees with their name and number make a gift they'll actually wear.",
    keywords:
      "gift ideas for NBA fans, basketball fan gifts, custom NBA hoodie, personalized basketball jersey, custom basketball gift with name and number",
    date: "2026-06-02",
    hero: "https://images.unsplash.com/photo-1607836127687-8eee1544cf50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    excerpt:
      "The best gift for a basketball fan is one made for them. Here's why a personalized NBA hoodie wins every time.",
    related: { label: "Shop NBA Hoodies & Tees", to: "/collections/nba" },
    sections: [
      {
        h: "The gift they'll actually wear",
        p: [
          "Mugs get shelved. Gift cards get forgotten. A personalized NBA hoodie with your friend's name and number on the back becomes part of their rotation — the piece they wear to every game and watch party.",
        ],
      },
      {
        h: "Make it personal",
        p: [
          "Choose their city's original team design, then add their name and a number that means something — their birth year, their old rec-league number, or their favorite player's. That small touch turns fan gear into a keepsake.",
        ],
      },
      {
        h: "Pick the perfect fit & color",
        p: [
          "Hoodies and tees come in adult and youth sizes and seven colors, so you can match their team's palette or their everyday wardrobe. Not sure on size? A tee is a safe, easy win.",
        ],
      },
      {
        h: "Order early, gift on time",
        p: [
          "Every piece is made to order and ships in about 3–7 business days. Order a week ahead of birthdays, the holidays, or the playoffs to be safe.",
        ],
      },
    ],
  },
  {
    slug: "custom-nfl-game-day-hoodies",
    title: "Custom NFL Game Day Hoodies: Rep Your Team",
    seoTitle: "Custom NFL Game Day Hoodies — Name & Number | Jersey Blend",
    description:
      "Build the perfect custom NFL game day hoodie: original team designs, your name and number on the back, and seven garment colors to choose from.",
    keywords:
      "custom NFL hoodie, football hoodie, game day hoodie, custom football jersey, personalized NFL shirt with name and number",
    date: "2026-06-03",
    hero: "https://images.unsplash.com/photo-1762013315117-1c8005ad2b41?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    excerpt:
      "Sundays hit different in gear made for you. Here's how to build a custom NFL game day hoodie worth wearing all season.",
    related: { label: "Shop NFL Football", to: "/collections/nfl" },
    sections: [
      {
        h: "Dress for the tailgate and the couch",
        p: [
          "A heavyweight hoodie is the MVP of game day — warm in the parking lot, comfortable on the couch, and loud in your team's colors. Our original football designs give you that stadium look without a licensed logo.",
        ],
      },
      {
        h: "Your name on the back",
        p: [
          "Add your name and number and wear it like a player. It's the detail that makes strangers at the tailgate ask where you got it.",
        ],
      },
      {
        h: "Seven colors, adult & youth sizes",
        p: [
          "Match your team with Navy, Red, Maroon and more, or keep it classic in Black. Youth sizes mean the whole family can rep together on Sundays.",
        ],
      },
    ],
  },
  {
    slug: "personalize-jersey-name-number",
    title: "How to Personalize Your Jersey with Your Name & Number",
    seoTitle: "How to Add Your Name & Number to a Custom Jersey | Jersey Blend",
    description:
      "A quick guide to personalizing a custom hoodie or tee with your name and number — how it prints, what looks best, and tips for the perfect back design.",
    keywords:
      "custom hoodie with name, personalized jersey with name and number, add your name and number jersey, custom jersey number shirt",
    date: "2026-06-04",
    hero: "https://images.unsplash.com/photo-1579269896398-4deb6cbdc320?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    excerpt:
      "Name and number on the back is what turns a graphic hoodie into your jersey. Here's how to get it right.",
    related: { label: "Start Customizing", to: "/#shop" },
    sections: [
      {
        h: "How the back print works",
        p: [
          "On every customizable design you'll see fields for a name and a number. We print the name arched across the shoulders with a large number beneath it — the classic sports-jersey layout — so it reads clearly from across the room.",
        ],
      },
      {
        h: "Tips for the best-looking back",
        list: [
          "Keep names to 12 characters or fewer so the letters stay big and bold.",
          "Use a 1–2 digit number for the most authentic jersey look.",
          "Darker garments (Black, Navy, Maroon) make bright designs pop the most.",
        ],
      },
      {
        h: "Preview before you buy",
        p: [
          "As you type, a live back preview updates instantly, and the 'Full Jersey' view shows the front design and your personalized back side by side — so there are no surprises when it arrives.",
        ],
      },
    ],
  },
];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
